import styled from '@emotion/styled';

import { tokens } from '@hotjar/hotsauce';
import { useCallback } from 'react';

import { useSlider } from '@hotjar/webapp/hooks/useSlider';

const BORDER_WIDTH = '2px';
const EDGE_WIDTH = '11px';

interface Position {
  end: number;
  start: number;
}

const Wrapper = styled.div`
  height: 24px;
  position: relative;
  width: 100%;
`;

const Frame = styled.section<{ position: Position }>`
  border: ${BORDER_WIDTH} solid ${tokens.color.white};
  border-radius: ${tokens.radius.m};
  overflow: hidden;
  // NOTE: to enable clicking through element that is transparent anyway but displayed above others
  pointer-events: none;
  position: absolute;

  bottom: 0;
  left: ${({ position }) => position.start}%;
  right: ${({ position }) => 100 - position.end}%;
  top: 0;
`;

const DragArea = styled.div<{ debug: boolean; isSliding: boolean; position: number }>`
  bottom: 0;
  left: 0;
  position: absolute;
  right: 0;
  top: 0;
  z-index: 2;
`;

const StartDragArea = styled(DragArea)`
  background-color: ${({ debug }) => (debug ? `rgba(255, 0, 0, 0.5)` : 'transparent')};
  cursor: w-resize;
  left: ${({ isSliding, position }) => (isSliding ? 0 : `${position}%`)};
  right: ${({ isSliding, position }) =>
    isSliding ? 0 : `calc(${100 - position}% - ${EDGE_WIDTH})`};
`;

const EndDragArea = styled(DragArea)`
  background-color: ${({ debug }) => (debug ? `rgba(0, 0, 255, 0.5)` : 'transparent')};
  cursor: e-resize;
  left: ${({ isSliding, position }) => (isSliding ? 0 : `calc(${position}% - ${EDGE_WIDTH})`)};
  right: ${({ isSliding, position }) => (isSliding ? 0 : `${100 - position}%`)};
`;

const Edge = styled.span<{ position: number }>`
  background-color: ${tokens.color.primary_cta};
  bottom: 0;
  position: absolute;
  top: 0;
  width: ${EDGE_WIDTH};
  // NOTE: it's enough to place the frame above other timeline elements; it needs to be done here as we don't want
  // the whole container to float above the timeline, so it remains clickable
  z-index: 2;

  &:after,
  &:before {
    background-color: ${tokens.color.white};
    content: '';
    height: ${tokens.spacer.x1};
    left: 48%;
    position: absolute;
    top: 50%;
    width: 1px;
  }

  &:after {
    transform: translateY(-50%) translateX(-1px);
  }

  &:before {
    transform: translateY(-50%) translateX(1px);
  }
`;

const StartEdge = styled(Edge)`
  border-bottom-left-radius: ${tokens.radius.m};
  border-top-left-radius: ${tokens.radius.m};
  left: ${({ position }) => position}%;
`;
const EndEdge = styled(Edge)`
  border-bottom-right-radius: ${tokens.radius.m};
  border-top-right-radius: ${tokens.radius.m};
  right: ${({ position }) => 100 - position}%;
`;

interface NewHighlightFrameProps {
  className?: string;
  debug?: boolean;
  onPositionChange: (newPositionChange: Position) => void;
  position: Position;
}

export function NewHighlightFrame({
  className,
  debug = false,
  onPositionChange,
  position,
}: NewHighlightFrameProps): JSX.Element {
  const handelStartChange = useCallback(
    (newStart) => {
      onPositionChange({
        ...position,
        start: newStart * 100,
      });
    },
    [onPositionChange, position],
  );
  const startSlider = useSlider<HTMLDivElement>({
    initialValue: position.start / 100,
    onScrub: handelStartChange,
  });

  const handleEndChange = useCallback(
    (newEnd) => {
      onPositionChange({
        ...position,
        end: newEnd * 100,
      });
    },
    [onPositionChange, position],
  );
  const endSlider = useSlider<HTMLDivElement>({
    initialValue: position.end / 100,
    onScrub: handleEndChange,
  });

  return (
    <Wrapper className={className}>
      <Frame position={position} />
      <StartEdge position={position.start} />
      <EndEdge position={position.end} />
      <StartDragArea
        debug={debug}
        isSliding={startSlider.isSliding}
        position={position.start}
        ref={startSlider.ref}
      />
      <EndDragArea
        debug={debug}
        isSliding={endSlider.isSliding}
        position={position.end}
        ref={endSlider.ref}
      />
    </Wrapper>
  );
}
